import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Layers, ShieldCheck, Sparkles, CheckCircle2, AlertCircle, Zap, FileDown,
} from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const STATUS_BADGE = {
    "COMPLETE":    "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
    "ENHANCEMENT": "border-amber-500/40   bg-amber-500/10   text-amber-400",
    "INTEGRATION": "border-blue-500/40    bg-blue-500/10    text-blue-400",
    "MISSING":     "border-red-500/40     bg-red-500/10     text-red-400",
};

export default function CapabilityMaturity() {
    const [data, setData] = useState(null);
    const [downloading, setDownloading] = useState(null);

    useEffect(() => {
        api.get("/edi/capability-maturity")
            .then((r) => setData(r.data))
            .catch(() => toast.error("Failed to load Capability Maturity Matrix"));
    }, []);

    const download = async (fmt) => {
        if (!data) return;
        setDownloading(fmt);
        try {
            const token = localStorage.getItem("sgsb.token");
            const resp = await fetch(`${API_BASE}/reports/export`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    title: "SGS Bateman · Executive Capability Maturity Matrix",
                    content: data.report_markdown,
                    fmt,
                    confidentiality: "Board",
                }),
            });
            if (!resp.ok) throw new Error(String(resp.status));
            const cd = resp.headers.get("Content-Disposition") || "";
            const match = cd.match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : `SGSBateman_Capability_Maturity.${fmt}`;
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 3000);
            toast.success(`Maturity Matrix · ${fmt.toUpperCase()} downloaded`);
        } catch { toast.error(`Maturity Matrix · ${fmt.toUpperCase()} export failed`); }
        finally { setDownloading(null); }
    };

    if (!data) return <div className="kicker text-slate-500">Loading Executive Capability Maturity Matrix…</div>;

    return (
        <div className="space-y-8" data-testid={EDI.capabilityMaturityRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™ · Phase 5</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Executive Capability Maturity Matrix™
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-3xl font-body text-sm">
                        Enterprise inventory of every workspace across the platform — verifying zero
                        duplication, executing the "Capture Once. Use Many Times.™" integration and
                        confirming production readiness under the Zero-Regression Policy.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <Layers className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">{data.total} capabilities inventoried</span>
                </div>
            </div>

            {/* Summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                {[
                    { l: "Total capabilities",     v: data.total,                          Icon: Layers },
                    { l: "Complete",               v: data.complete,                       Icon: CheckCircle2, colour: "text-emerald-400" },
                    { l: "Enhancement",            v: data.enhancement,                    Icon: AlertCircle,  colour: "text-amber-400" },
                    { l: "Integration",            v: data.integration,                    Icon: Zap,          colour: "text-blue-400" },
                    { l: "Average maturity",       v: `${data.avg_maturity_pct}%`,         Icon: ShieldCheck,  colour: "text-[var(--sgsb-gold)]" },
                    { l: "Duplicate workspaces",   v: data.duplicates.length,              Icon: Sparkles,     colour: "text-emerald-400" },
                ].map(({ l, v, Icon, colour }) => (
                    <div key={l} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                        <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                        <Icon className={`h-4 w-4 ${colour || "text-[var(--sgsb-gold)]"}`} strokeWidth={1.5} />
                        <div className="mt-2 kicker text-slate-400 text-[10px]">{l}</div>
                        <div className="mt-2 font-display font-black text-white text-xl tracking-tight">{v}</div>
                    </div>
                ))}
            </div>

            {/* Capture-once surfaces */}
            <div className="glass rounded-xl p-6">
                <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                    <h3 className="font-display font-black text-white tracking-tight">Capture Once. Use Many Times.™ — {data.capture_once_surfaces.length} downstream surfaces</h3>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    {data.capture_once_surfaces.map((s) => (
                        <span key={s} className="rounded-full border border-slate-700 bg-slate-950/40 text-slate-300 text-xs px-3 py-1">{s}</span>
                    ))}
                </div>
            </div>

            {/* Matrix table */}
            <div className="glass rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                    <Layers className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                    <h3 className="font-display font-black text-white tracking-tight">Capability inventory</h3>
                    <div className="flex-1" />
                    <div className="flex flex-wrap gap-2">
                        {[["pdf","PDF"],["docx","Word"],["pptx","PowerPoint"]].map(([fmt, label]) => (
                            <button key={fmt}
                                data-testid={EDI.capabilityMaturityExport(fmt)}
                                onClick={() => download(fmt)} disabled={downloading}
                                className="inline-flex items-center gap-2 rounded-md border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)] px-3 py-1.5 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold)]/10 disabled:opacity-40 transition-colors duration-200"
                            >
                                <FileDown className="h-3 w-3" strokeWidth={2} />
                                {downloading === fmt ? "…" : label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-body">
                        <thead>
                            <tr className="text-slate-500 kicker text-[10px]">
                                <th className="text-left px-4 py-3">Capability</th>
                                <th className="text-left px-4 py-3">Workspace</th>
                                <th className="text-left px-4 py-3">Phase</th>
                                <th className="text-right px-4 py-3">Maturity</th>
                                <th className="text-right px-4 py-3">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.matrix.map((r, i) => (
                                <tr key={`${r.capability}-${i}`} className="border-t border-slate-800/60 align-top">
                                    <td className="px-4 py-3 text-slate-100">
                                        <div>{r.capability}</div>
                                        {r.note && (
                                            <div className="mt-1 text-[11px] text-slate-400 font-body">{r.note}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-slate-300"><code className="text-[11px]">{r.workspace}</code></td>
                                    <td className="px-4 py-3 text-slate-400">{r.phase}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex items-center gap-2">
                                            <div className="h-1.5 w-24 rounded-full bg-slate-800 overflow-hidden">
                                                <div className="h-full bg-[var(--sgsb-gold)]" style={{ width: `${r.maturity}%` }} />
                                            </div>
                                            <span className="text-[var(--sgsb-gold)] font-display font-bold text-sm">{r.maturity}%</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] border ${STATUS_BADGE[r.status] || STATUS_BADGE.COMPLETE}`}>{r.status}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
